import * as Sentry from '@sentry/gatsby';
import {
  styled,
  Typography,
  useTheme,
  TypographyProps,
  Grid,
} from '@mui/material';
import React, { useState } from 'react';
import theme from '../../theme';
import EmailInput from './emailInput';
import Modal from '../modal';
import Button from '../button';
import Notification from '../Notification';
import useCreateStockNotification from '../../hooks/useCreateStockNotification';

interface ModalBackInStockProps {
  productName: string;
  productID: string;
  open: boolean;
  onClose: () => void;
}

const EmailInputContainer = styled('div')`
  margin-bottom: ${theme.spacing(6)};
  width: 312px;
`;

const SubHeadContainer = styled('div')`
  margin-bottom: 32px;
`;

const ContentContainer = styled('div')`
  margin-bottom: 24px;
`;

const CustomTypography: React.FC<
  TypographyProps & {
    children?: React.ReactNode;
    text?: string;
  }
> = ({ children, text, ...props }) => {
  const thisTheme = useTheme();
  return (
    <Typography color={thisTheme.palette.greyscale.white} {...props}>
      {children ?? text}
    </Typography>
  );
};

const StyledLabel = styled(CustomTypography)`
  color: ${theme.palette.greyscale.lightGrey};
  font-family: 'HCo Gotham SSm';
  font-style: normal;
  font-weight: 300;
  font-size: 12px;
  line-height: 18px;
`;

export const ModalBackInStock: React.FC<ModalBackInStockProps> = ({
  productName,
  productID,
  open,
  onClose,
}) => {
  const [buttonStatus, setButtonStatus] = React.useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  // disables + enables input fields during info submission
  const [submitting, setSubmitting] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const [notificationInfo, setNotificationInfo] = useState('');

  // use gql hook
  const [createStockNotification] = useCreateStockNotification();

  const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
    setSubmitting(true);
    event.preventDefault();

    // submit notification request
    createStockNotification({
      variables: {
        input: {
          productId: productID,
          variantId: null,
          quantity: 1,
          email: notificationInfo,
          acceptsMarketing: false,
          utcOffsetSeconds: new Date().getTimezoneOffset() * 60,
        },
      },
    })
      .then(() => {
        setShowSuccessMessage(true);
        setShowButton(false);
      })
      .catch((createStockNotifError) => {
        setShowErrorMessage(true);
        setShowButton(false);
        Sentry.captureException(createStockNotifError);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Modal
      title="Notify Me When Available"
      open={open}
      onClose={() => {
        onClose();
      }}
    >
      <SubHeadContainer>
        <CustomTypography
          variant="bodyL"
          text="Let us know how we should notify you when this product is back in stock."
        />
      </SubHeadContainer>

      <ContentContainer>
        <StyledLabel text="Product" />
        <CustomTypography variant="bodyM" text={productName} />
      </ContentContainer>

      <EmailInputContainer>
        <EmailInput
          submitReady={(readyForSubmit) => {
            setButtonStatus(readyForSubmit);
          }}
          updateEmail={(inputVal) => {
            setNotificationInfo(inputVal);
          }}
          disabled={submitting}
        />
      </EmailInputContainer>

      {showButton && (
        <Button
          disabled={buttonStatus}
          variant={buttonStatus ? 'outline' : 'primary'}
          color="cyan"
          text="Notify Me When Available"
          onClick={(event) => handleOnClick(event)}
        />
      )}

      {showSuccessMessage && (
        <Grid
          item
          xs={12}
          sx={{
            marginLeft: '0px',
            marginTop: '-2rem',
            '&.MuiGrid-item': {
              paddingTop: '0px',
            },
          }}
        >
          <Notification
            variant="standard"
            severity="success"
            sx={{
              paddingTop: '0px',
              color: theme.palette.success.main,
              marginLeft: '-1rem',
            }}
          >
            Your notification is registered. You may close this form.
          </Notification>
        </Grid>
      )}

      {showErrorMessage && (
        <Grid
          item
          xs={12}
          sx={{
            marginLeft: '0px',
            marginTop: '-2rem',
            '&.MuiGrid-item': {
              paddingTop: '0px',
            },
          }}
        >
          <Notification
            variant="standard"
            severity="error"
            sx={{
              paddingTop: '0px',
              color: theme.palette.error.main,
              marginLeft: '-1rem',
            }}
          >
            Error trying to register your notification. Please try again later.
          </Notification>
        </Grid>
      )}
    </Modal>
  );
};
export default ModalBackInStock;
